import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";

// SurveyJS Creator
import "survey-core/defaultV2.min.css";
import "survey-creator-core/survey-creator-core.min.css";
import "./surveyCreatorOverrides.css";
import { Serializer } from "survey-core";
import { SurveyCreator, SurveyCreatorComponent } from "survey-creator-react";

// Register custom property for slider: hide value from form filler (value still sent to backend)
if (!Serializer.getProperty("slider", "hideValueFromFiller")) {
  Serializer.addProperty("slider", {
    name: "hideValueFromFiller",
    type: "boolean",
    category: "general",
    default: false,
    visibleIndex: 10,
    displayName: "Hide value from form filler",
    description: "If enabled, the selected number is not shown to the respondent; the value is still saved for backend processing.",
  });
}

const DEBUG = true;
const log = (...args) => DEBUG && console.log("[CustomFormBuilder]", ...args);
log("module loaded");

const defaultSurveyJson = {
  pages: [
    {
      name: "page1",
      elements: [],
    },
  ],
};

/**
 * Form builder using SurveyJS Survey Creator.
 * data: survey JSON object (SurveyJS schema with pages/elements).
 * onChange: (json) => void when schema changes.
 */
function CustomFormBuilder({ data, onChange }) {
  const [creator, setCreator] = useState(null);
  const [error, setError] = useState(null);
  const initialJsonRef = useRef(data);
  const lastAppliedDataRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const mountedRef = useRef(true);
  log("render", { creator: !!creator, hasData: !!data?.pages, dataPagesLength: data?.pages?.length });

  // Keep initialJsonRef in sync with data so async init uses latest (e.g. edit load)
  useEffect(() => {
    initialJsonRef.current = data;
  }, [data]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    log("init useEffect ran");
    mountedRef.current = true;
    let cancelled = false;

    function initCreator() {
      log("initCreator() called");
      try {
        // Shared survey theme variables (creator + runtime)
        const { surveyThemeVariables } = require("../../surveyTheme");
        const options = {
          autoSaveEnabled: true,
          collapseOnDrag: true,
          isAutoSave: true, // hides save icon in toolbar
          themeVariables: surveyThemeVariables,
        };
        log("creating SurveyCreator...");
        const c = new SurveyCreator(options);
        log("SurveyCreator created");

        c.saveSurveyFunc = (saveNo, callback) => {
          try {
            const json = c.JSON;
            if (onChangeRef.current) onChangeRef.current(json);
          } catch (e) {
            console.warn("SurveyCreator saveSurveyFunc:", e);
          }
          callback(saveNo, true);
        };

        // Use latest data from ref (critical for edit: loaded schema is set by parent before init runs)
        const latest = initialJsonRef.current;
        let initial = defaultSurveyJson;
        if (
          latest &&
          typeof latest === "object" &&
          !Array.isArray(latest) &&
          Array.isArray(latest.pages)
        ) {
          initial = latest;
          log("initCreator using loaded schema, pages:", latest.pages?.length);
        } else {
          log("initCreator using default empty schema (no/invalid data in ref)");
        }
        c.JSON = initial;

        log("calling setCreator, cancelled=", cancelled, "mountedRef=", mountedRef.current);
        if (!cancelled && mountedRef.current) setCreator(c);
      } catch (e) {
        console.error("[CustomFormBuilder] Survey Creator init error:", e);
        if (!cancelled && mountedRef.current) setError(e?.message || "Failed to load form builder");
      }
    }

    // Wait for layout then create creator (one frame + short delay so container has size)
    let timeoutId;
    log("scheduling requestAnimationFrame");
    const rafId = requestAnimationFrame(() => {
      log("requestAnimationFrame fired, scheduling setTimeout(50)");
      timeoutId = setTimeout(initCreator, 50);
    });

    return () => {
      log("cleanup");
      cancelled = true;
      mountedRef.current = false;
      cancelAnimationFrame(rafId);
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, []);

  // When creator is ready or data changes (e.g. loaded schema on edit), ensure creator shows it
  useEffect(() => {
    if (!creator || !data || !Array.isArray(data.pages)) return;
    const dataSig = JSON.stringify(data);
    if (lastAppliedDataRef.current === dataSig) return;
    lastAppliedDataRef.current = dataSig;
    try {
      creator.JSON = data;
      log("applied data to creator, pages:", data.pages?.length);
    } catch (e) {
      console.warn("[CustomFormBuilder] apply data to creator failed:", e);
      lastAppliedDataRef.current = null;
    }
  }, [creator, data]);

  if (error) {
    return (
      <Box
        sx={{
          height: "100%",
          minHeight: 400,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          p: 2,
        }}
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!creator) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          minHeight: 400,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body2" color="text.secondary">
          Loading form builder…
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className="survey-creator-wrapper survey-creator-hide-license"
      sx={{
        width: "100%",
        height: "100%",
        minHeight: 450,
        position: "relative",
        "& .svc-creator": { height: "100% !important" },
        "& .svc-creator__content": { height: "100% !important" },
        "& .svc-creator > div": { height: "100% !important" },
        "& .sd-root-modern": { minHeight: "100%" },
      }}
    >
      <div style={{ width: "100%", height: "100%", minHeight: 450 }}>
        <SurveyCreatorComponent
          creator={creator}
          style={{ width: "100%", height: "100%", minHeight: 450 }}
        />
      </div>
    </Box>
  );
}

export default CustomFormBuilder;
